/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
import { Events } from '../interaction/Events';
import { Scene as ThreeScene, Group } from 'three';
import { SceneGraphPath } from './SceneGraphPath';
import { Objects } from '../objects/Objects';
/**
 * Representation of a single scene
 * see ThreeScene for additional information
 */
export class Scene extends ThreeScene {
    /**
     * constructor
     * @param stage the stage that contains this scene
     */
    constructor(stage) {
        super();
        this._tags = new Map();
        this._stage = stage;
    }
    /**
     * Set the given tag to the given objects
     * @param tag tag to be set
     * @param objects objects the tag is applied to
     */
    setTag(tag, ...objects) {
        if (!!tag) {
            let array;
            if (this._tags.has(tag)) {
                array = this._tags.get(tag);
            }
            else {
                array = new Array();
                this._tags.set(tag, array);
            }
            objects.filter((obj) => array.indexOf(obj) < 0).forEach((obj) => array.push(obj));
        }
    }
    /**
     * removes a tag without removing the tagged objects
     * @param tag
     */
    removeTag(tag) {
        this._tags.delete(tag);
    }
    /**
     * executes a function for all objects with a special tag
     * @param tag the tag
     * @param func the function
     */
    doOnTaggedObjects(tag, func) {
        const hasTaggedObj = this._tags.has(tag);
        if (hasTaggedObj) {
            const array = this._tags.get(tag);
            // inverse loop 'cause safe item remove
            for (let i = array.length - 1; i >= 0; i--) {
                func(array[i]);
            }
        }
        return hasTaggedObj;
    }
    /**
     *
     * @param parent
     * @param objects
     */
    addToParent(parent, ...objects) {
        let parentObj;
        parentObj = SceneGraphPath.getObjectByPath(this, parent);
        if (!parentObj) { // if object with this name not existend than create it
            parentObj = new Group();
            parentObj.name = parent;
            this.set(parentObj);
        }
        this._setUnsafe(parentObj, ...objects);
    }
    /**
     * adds a list of objects (or a single one) to the scene
     * please consider that all objects already inserted with names will be replaced by objects with the same name
     * unnamed objects can be put into a scene several times
     * @param objects
     */
    add(...objects) {
        this._setUnsafe(this, ...objects.filter((o) => !!o.name)); // set all Objects with name
        const noNameObjects = objects.filter((obj) => !obj.name); // collect Objects without name
        // three js checks if the length of the add argument is > 1
        // if not it is handled as an instance of Object3D.
        // Calling super.add() with an empty array leads to a console.error from THREE.js
        if (noNameObjects.length > 0) {
            super.add(...noNameObjects);
        }
        // XXX iterators are unstable with babel
        // for (const obj of noNameObjects) {
        //   this.getStage().dispatchEvent(Events.OBJECTADDED, { detail: { name: undefined, id: obj.id } });
        // }
        Array.from(noNameObjects).forEach((obj) => {
            this.getStage().dispatchEvent(Events.OBJECTADDED, { detail: { name: undefined, id: obj.id } });
        });
        return this;
    }
    /**
     * sets objects (only if a name exist) to the scene
     * please consider that all objects already inserted with names will be replaced by objects with the same name
     * @param objects
     */
    set(...objects) {
        const withName = objects.filter((o) => !!o.name);
        if (withName.length !== objects.length) {
            // tslint:disable-next-line:no-console
            console.warn('Scene.set(...) : unnamed objects ignored');
        }
        this._setUnsafe(this, ...withName);
    }
    /**
     * removes a list of objects (or a single one) from the scene
     * @param objects
     */
    remove(...objects) {
        super.remove(...objects);
        for (const obj of objects) {
            this._removeObjectFromTags(obj);
            Objects.dispose(obj);
            this.getStage().dispatchEvent(Events.OBJECTREMOVED, { detail: { name: obj.name, id: obj.id } });
        }
        return this;
    }
    /**
     * returns its stage
     */
    getStage() {
        return this._stage;
    }
    _setUnsafe(parent, ...objects) {
        objects.forEach((newObj) => {
            const obj = this.getObjectByName(newObj.name);
            let replaced = false;
            if (!!obj) {
                this._remove(obj.parent, obj);
                this._removeObjectFromTags(obj);
                Objects.dispose(obj);
                replaced = true;
            }
            this._add(parent, newObj);
            this.getStage().dispatchEvent(replaced ? Events.OBJECTREPLACED : Events.OBJECTADDED, { detail: { name: newObj.name, id: newObj.id } });
        });
    }
    _add(parent, object) {
        if (parent === this) {
            super.add(object);
        }
        else {
            parent.add(object);
        }
    }
    _remove(parent, object) {
        if (parent === this) {
            super.remove(object);
        }
        else {
            parent.remove(object);
        }
    }
    // tslint:disable-next-line:no-any
    /* private _disposeObjects(obj: any) {
       if (!!obj.geometry) {
         obj.geometry.dispose();
       }
       if (!!obj.material) {
         if (Array.isArray(obj.material)) {
           (obj.material as Array<Material>).forEach((m) => m.dispose());
         } else {
           (obj.material as Material).dispose();
         }
       }
       if (!!obj.texture) {
         obj.texture.dispose();
       }
     }*/
    _forTagsWith(object, func) {
        this._tags.forEach((taggedObjects) => {
            taggedObjects.forEach((taggedObject, i) => {
                if (taggedObject.id === object.id) {
                    func(taggedObjects, i);
                }
            });
        });
    }
    _removeObjectFromTags(object) {
        this._forTagsWith(object, (taggedObjects, i) => taggedObjects.splice(i, 1));
    }
}
//# sourceMappingURL=Scene.js.map